import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Building2, Users, Heart, CheckCircle2, ArrowRight, TrendingUp } from "lucide-react";
import { API_URL, BRAND } from "../lib/brand";

export default function RegalaCuraAziende() {
  const [form, setForm] = useState({
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    company: "",
    team_size: "",
    service_type: "consulenza-singola",
    delivery_notes: "",
    consent: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_email || !form.company || !form.team_size || !form.consent) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setSending(true);
    try {
      // Reuse the voucher/request endpoint with a special notes+recipient marker
      await axios.post(`${API_URL}/voucher/request`, {
        buyer_name: form.buyer_name,
        buyer_email: form.buyer_email,
        buyer_phone: form.buyer_phone,
        service_type: form.service_type,
        occasion: "generico",
        recipient_name: `Team ${form.company}`,
        recipient_email: "",
        personal_message: `Richiesta B2B da ${form.company} · ${form.team_size} persone`,
        delivery: "digital",
        payment_preference: "bonifico",
        notes: `AZIENDA: ${form.company}\nTeam: ${form.team_size} persone\nRichiesta: ${form.delivery_notes || "—"}`,
        consent: true,
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="regala-aziende-page">
      {/* HERO */}
      <section className="relative overflow-hidden px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20 bg-gradient-to-br from-[#3D2140] to-[#164244] text-white">
        <div className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-[#FF2768]/20 blur-3xl" />
        <div className="max-w-6xl mx-auto relative">
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.35em] text-[#FFE45C] hover:underline mb-8">
            ← Torna al sito
          </Link>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 border border-white/20 mb-6">
              <Building2 size={14} className="text-[#FFE45C]" />
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFE45C] font-bold">Per le aziende</span>
            </div>

            <h1 className="font-display italic text-5xl md:text-7xl lg:text-8xl leading-[0.9] max-w-4xl">
              Regala Cura ai tuoi <span className="text-[#FF2768]">collaboratori</span>
            </h1>
            <p className="mt-8 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
              Un welfare aziendale che parla la lingua della cura. <strong className="text-[#FFE45C]">Voucher personalizzati</strong> per genitori e famiglie in azienda — consulenze, formazione, presenza.
            </p>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/10">
                <TrendingUp size={20} className="text-[#FFE45C] mb-2" />
                <p className="font-display italic text-2xl">-30% turnover</p>
                <p className="text-xs text-white/70 mt-1">Il welfare famigliare riduce il turnover post-maternità*</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/10">
                <Heart size={20} className="text-[#FF2768] mb-2" />
                <p className="font-display italic text-2xl">+82% engagement</p>
                <p className="text-xs text-white/70 mt-1">I collaboratori si sentono visti come persone, non ruoli</p>
              </div>
              <div className="bg-white/10 backdrop-blur rounded-2xl p-5 border border-white/10">
                <Users size={20} className="text-[#81C3C9] mb-2" />
                <p className="font-display italic text-2xl">Detraibile</p>
                <p className="text-xs text-white/70 mt-1">Il welfare aziendale rientra nei benefit deducibili</p>
              </div>
            </div>

            <div className="mt-10">
              <a href="#richiesta" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#e91e5e]" data-testid="cta-scroll-form">
                Chiedimi un preventivo <ArrowRight size={14} />
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SUCCESS */}
      {sent && (
        <section className="px-6 md:px-10 py-12">
          <div className="max-w-3xl mx-auto bg-white rounded-3xl p-8 md:p-10 border border-[#FF2768]/20 shadow-xl" data-testid="aziende-success">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-full bg-[#FF2768] text-white flex items-center justify-center flex-shrink-0">
                <CheckCircle2 size={24} />
              </div>
              <div className="flex-1">
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-bold">Richiesta ricevuta</p>
                <h3 className="font-display italic text-3xl text-[#3D2140] mt-2">Grazie, ti scrivo entro 24 ore.</h3>
                <p className="mt-3 text-[#3D2140]/75 leading-relaxed">
                  Ho ricevuto la tua richiesta. Ti scriverò io con una proposta personalizzata per il numero di collaboratori e il tipo di percorso.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* PACCHETTI */}
      <section className="px-6 md:px-10 py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-bold text-center">— Pacchetti welfare</p>
          <h2 className="mt-3 font-display italic text-4xl md:text-5xl text-[#3D2140] text-center max-w-3xl mx-auto">Il benefit che le neomamme (e neopapà) ricordano davvero</h2>
          <p className="mt-4 text-center text-[#3D2140]/70 max-w-2xl mx-auto">
            Il rientro dalla maternità o paternità è il momento più fragile della carriera. Un percorso dedicato con me può fare la differenza tra restare e andarsene.
          </p>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: "Piccola azienda", size: "5–15 persone", features: ["Voucher consulenza singola per neogenitori", "Workshop live 1 sessione", "PDF risorse personalizzati"], accent: "#81C3C9" },
              { name: "Media impresa", size: "15–50 persone", features: ["Voucher pacchetto 3 consulenze", "Workshop live 2 sessioni + Q&A", "Accesso Biblioteca T.A.T.A. per tutti", "Contatto WhatsApp diretto"], accent: "#FFE45C", featured: true },
              { name: "Corporate", size: "50+ persone", features: ["Voucher visita a domicilio", "Percorso formazione tate aziendali", "Programma mensile personalizzato", "Report benessere anonimo"], accent: "#FF2768" },
            ].map((pkg) => (
              <div key={pkg.name} className={`relative rounded-3xl p-8 ${pkg.featured ? "bg-[#3D2140] text-white shadow-2xl scale-105" : "bg-[#FAF8F5] border border-[#3D2140]/8"}`}>
                {pkg.featured && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#FFE45C] text-[#3D2140] text-[10px] uppercase tracking-widest font-bold">Più richiesto</span>
                )}
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ background: pkg.accent + "40" }}>
                  <Users size={20} style={{ color: pkg.accent }} />
                </div>
                <h3 className={`font-display text-2xl ${pkg.featured ? "text-white" : "text-[#3D2140]"}`}>{pkg.name}</h3>
                <p className={`text-sm mt-1 ${pkg.featured ? "text-white/70" : "text-[#3D2140]/60"}`}>{pkg.size}</p>
                <ul className="mt-6 space-y-3">
                  {pkg.features.map((f, i) => (
                    <li key={i} className={`flex items-start gap-2 text-sm ${pkg.featured ? "text-white/90" : "text-[#3D2140]/80"}`}>
                      <CheckCircle2 size={14} style={{ color: pkg.accent }} className="mt-0.5 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <p className={`mt-6 text-xs italic ${pkg.featured ? "text-[#FFE45C]" : "text-[#4E9EA5]"}`}>Preventivo su misura — scrivimi per parlarne</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="richiesta" className="px-6 md:px-10 py-16 md:py-24 bg-[#FDECEF]">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-[10px] uppercase tracking-[0.4em] text-[#FF2768] font-bold">— Richiesta preventivo</p>
            <h2 className="mt-3 font-display italic text-4xl md:text-5xl text-[#3D2140]">Parliamone insieme</h2>
            <p className="mt-4 text-[#3D2140]/70">Compila con i tuoi dati e le dimensioni del team. Ti mando una proposta personalizzata via email entro 24 ore.</p>
          </div>

          <form onSubmit={submit} className="bg-white rounded-3xl p-6 md:p-10 space-y-5 border border-[#3D2140]/8" data-testid="aziende-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Nome e cognome *</label>
                <input value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-b2b-name" />
              </div>
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Ruolo</label>
                <input value={form.role || ""} onChange={(e) => setForm({ ...form, role: e.target.value })} placeholder="HR Manager, CEO..." className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-b2b-role" />
              </div>
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Azienda *</label>
                <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-b2b-company" />
              </div>
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Dimensione team *</label>
                <select value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-b2b-size">
                  <option value="">Scegli...</option>
                  <option value="1-5">1–5 persone</option>
                  <option value="5-15">5–15 persone</option>
                  <option value="15-50">15–50 persone</option>
                  <option value="50-100">50–100 persone</option>
                  <option value="100+">Più di 100</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Email aziendale *</label>
                <input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" required data-testid="input-b2b-email" />
              </div>
              <div>
                <label className="block text-xs text-[#3D2140]/70 mb-1.5">Telefono</label>
                <input value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} className="w-full px-4 py-2.5 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none" data-testid="input-b2b-phone" />
              </div>
            </div>

            <div>
              <label className="block text-xs text-[#3D2140]/70 mb-1.5">Che tipo di percorso ti immagini?</label>
              <textarea value={form.delivery_notes} onChange={(e) => setForm({ ...form, delivery_notes: e.target.value })} rows={4} placeholder="es. Vorremmo offrire voucher consulenza a chi rientra dalla maternità, oppure organizzare workshop mensili..." className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-[#3D2140]/10 text-sm focus:border-[#FF2768] outline-none resize-none" data-testid="input-b2b-notes" />
            </div>

            <label className="flex items-start gap-3 cursor-pointer pt-2">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 w-4 h-4 accent-[#FF2768]" required data-testid="input-b2b-consent" />
              <span className="text-xs text-[#3D2140]/75 leading-relaxed">Confermo di aver letto la <Link to="/privacy" className="underline text-[#FF2768]">privacy policy</Link> e acconsento al trattamento dei dati.</span>
            </label>

            <button type="submit" disabled={sending} className="w-full py-4 rounded-full bg-[#3D2140] text-white font-bold uppercase tracking-widest text-sm hover:bg-[#164244] disabled:opacity-40 transition-all" data-testid="btn-b2b-submit">
              {sending ? "Sto inviando..." : "🏢 Chiedimi un preventivo"}
            </button>
            <p className="text-center text-xs text-[#3D2140]/50">
              Preferisci una call?{" "}
              <a href={BRAND.whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-[#FF2768] underline">Scrivimi su WhatsApp</a>
            </p>
          </form>

          <p className="text-center mt-6 text-[10px] italic text-[#3D2140]/40">* Dati indicativi basati su studi welfare 2024–2025</p>
        </div>
      </section>
    </div>
  );
}
